// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { Action, BackendAction, processBackendAction } from '../../models/Action';
import Contact from '../../models/Contact';
import FundOpportunity from '../../models/FundOpportunity';
import {
  CreateInvestmentEntityRequest,
  InvestmentEntity,
  InvestorAllocationsResponse,
} from '../../models/InvestmentEntity';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import Investor from '../../models/Investor';
import { PlaceholderContactWithMatchesProps } from '../../models/PlaceholderContact';
export const adminBackendServiceEntityMixin = {
  getInvestmentEntitiesForUserAsOwner(contact: Contact): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      `contacts/${contact.id}/investment-entities/owner`,
      'Unable to retrieve investment entities',
      (json) => json.data || [],
    );
  },

  getInvestmentEntitiesForUser(contactId: string): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      `contacts/${contactId}/investment-entities`,
      'Unable to retrieve investment entities',
      (json) => json.data || [],
    );
  },

  createInvestmentEntity(
    entityData: import('../../models/InvestmentEntity').CreateInvestmentEntityRequest,
    _investor?: import('../../models/Investor').default,
  ): Promise<import('../../models/InvestmentEntity').InvestmentEntity> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('investment-entities'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(entityData),
      });
      if (!response.ok) {
        const data = await response.json();
        throw data.error?.message || 'Unable to create investment entity';
      }
      const data = await response.json();
      return data.data;
    }).catch((error) => {
      throw this.handleError(error);
    });
  },

  updateInvestmentEntity(
    entityId: string,
    entityData: { name?: string; description?: string; isForeignInvestor?: boolean },
  ): Promise<InvestmentEntity> {
    return this.authApiMutateAuthed(
      `investment-entities/${entityId}`,
      'PUT',
      entityData,
      (json) => json.data,
    );
  },

  createAndAssociateInvestmentEntity(
    investorId: string,
    entityData: CreateInvestmentEntityRequest,
  ): Promise<InvestmentEntity> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(`investors/${investorId}/investment-entity`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(entityData),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw this.handleError(data);
      }
      return this.getInvestmentEntity(data.data.id);
    });
  },

  addContactToEntity(
    entityId: string,
    contactId: string,
    role: string = 'OWNER',
    representativeType?: string,
  ): Promise<void> {
    const body: Record<string, string> = { role };
    if (representativeType) {
      body.representativeType = representativeType;
    }
    return this.authApiMutateAuthed<void>(
      `investment-entities/${entityId}/contacts/${contactId}`,
      'POST',
      body,
      () => undefined,
    );
  },

  removeContactFromEntity(entityId: string, contactId: string): Promise<void> {
    return this.authApiDeleteVoid(`investment-entities/${entityId}/contacts/${contactId}`);
  },

  getInvestorAllocations(
    investorId: string,
    includePlaceholder: boolean,
  ): Promise<InvestorAllocationsResponse> {
    return this.authApiGetAuthed(
      `investors/${investorId}/allocations?includePlaceholder=${includePlaceholder}`,
      'Unable to fetch investor allocations',
      (json) => json.data || json,
    );
  },

  getEntityContacts(entityId: string): Promise<InvestmentEntityContact[]> {
    return this.authApiGetAuthed(
      `investment-entities/${entityId}/contacts`,
      'Unable to retrieve entity contacts',
      (json) => json.data || [],
    );
  },

  searchContactsForEntity(searchTerm: string, entityId?: string): Promise<Contact[]> {
    const query = entityId
      ? `contacts/search?term=${encodeURIComponent(searchTerm)}&excludeEntityId=${encodeURIComponent(entityId)}`
      : `contacts/search?term=${encodeURIComponent(searchTerm)}`;
    return this.authApiGetAuthed(
      query,
      'Network error while searching contacts for entity',
      (json) => (json.data ?? []) as Contact[],
    );
  },

  getAllInvestmentEntities(): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      'investment-entities',
      'Unable to retrieve investment entities',
      (json) => json.data || [],
    );
  },

  searchInvestmentEntitiesDirect(searchTerm: string): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      `investment-entities/search?term=${encodeURIComponent(searchTerm)}`,
      'Unable to search investment entities',
      (json) => json.data || [],
    );
  },

  searchFundOpportunitiesAvailableForContact(
    searchTerm: string,
    contactId: string,
  ): Promise<FundOpportunity[]> {
    return this.authApiGetAuthed(
      `fundOpportunities/available-for-contact/${contactId}?term=${encodeURIComponent(searchTerm)}`,
      'Unable to search available fund opportunities',
      (json) => json.data || [],
    );
  },

  getOutstandingActions(): Promise<Action[]> {
    return this.authApiGetAuthed(
      'outstandingActions',
      'Unable to retrieve outstanding actions',
      (json) => json.data.map((action: BackendAction) => processBackendAction(action)) || [],
    );
  },

  getPlaceholderContact(placeholderId: string): Promise<PlaceholderContactWithMatchesProps> {
    return this.authApiGetAuthed(
      `investment-entities/placeholder-contacts/${placeholderId}`,
      'Unable to retrieve placeholder contact',
      (json) => json.data || json,
    );
  },

  replacePlaceholderContact(placeholderId: string, contactId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investment-entity/placeholder/${placeholderId}/replace`,
      'PUT',
      { contactId },
      () => undefined,
    );
  },

  updatePlaceholderContact(
    placeholderContactId: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      role?: string;
      representativeType?: string;
      otherRepresentativeType?: string;
    },
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investment-entities/placeholder-contacts/${placeholderContactId}`,
      'PATCH',
      data,
      () => undefined,
    );
  },

  promotePlaceholderContact(placeholderId: string): Promise<{ contact: { id: string } }> {
    return this.authApiMutateAuthed(
      `investment-entity/placeholder/${placeholderId}/promote`,
      'POST',
      undefined,
      (json) => json.data,
    );
  },

  deleteInvestmentEntity(entityId: string): Promise<void> {
    return this.authApiDeleteVoid(`investment-entities/${entityId}`);
  },
} satisfies ThisType<BaseBackendService>;
